import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Role } from "../models/admin/role";
import { RoleListing } from "../models/admin/roles-listing";
import { UserV2 } from "../models/admin/user-v2";
import { UserListing } from "../models/admin/user-listing";
import { TableData } from "../models/table-data";
import { FileGroup } from "../models/admin/file-group";
import { FileGroupListing } from "../models/admin/file-group-listing";
import { TenantService } from "./tenant.service";
import { IntegrationDetails } from "../models/integration-details";
import { Permissions } from "../models/admin/permissions/permissions";
import { FileGroupPermissionListing } from "../models/admin/permissions/file-group-permission-listing";
import { IntergrationPermissionListing } from "../models/admin/permissions/intergration-permission-listing";
import { TablePermissionListing } from "../models/admin/permissions/table-permission-listing";
import { ProjectGroupPermissionListing } from "../models/admin/permissions/project-group-permission-listing";
import { MyAccess } from "../models/my-access";
import { AuthService } from "./auth-service.service";
import { ConfigService } from './config.service';
import { BehaviorSubject, Observable, firstValueFrom } from "rxjs";
import { take } from "rxjs/operators";
import { StringLocalizerService } from "./string-localizer.service";
import { UserClaim } from "../models/cookie-auth/user-claim";
import { LoaderService } from "./loader.service";
import { LoggerService } from './logger.service';


@Injectable({
    providedIn: 'root'
})

export class AdminService {
    private readonly logger = inject(LoggerService);
    public users: UserV2[] = [];
    roles: Role[] = [];
    fileGroups: FileGroup[] = [];
    tables: TableData[] = [];
    integrations: IntegrationDetails[];
    myAccess: MyAccess | null = null;
    $myAccess: BehaviorSubject<MyAccess | null> = new BehaviorSubject<MyAccess | null>(null);
    public baseUrl: string;
    clientNameSpace: string;
    $disableAdminButtons: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    
    public selectedRole: Role | null = null;
    public $selectedRole: BehaviorSubject<Role | null> = new BehaviorSubject<Role | null>(null);
    public updatedRoles: Role[] = [];
    public $updatedRoles: BehaviorSubject<Role[]> = new BehaviorSubject<Role[]>([]);

    constructor(
        private readonly http: HttpClient,
        private readonly tenantService: TenantService,
        private readonly authService: AuthService,
        private readonly configService: ConfigService,
        private readonly loaderService: LoaderService,
        private readonly stringLocalizerService: StringLocalizerService) {
        this.configService.$configuration.subscribe(config => {
            if (config != null) {
                this.baseUrl = config.servicesUrl;
            }
        });
        this.authService.$userClaims.subscribe(claims => {
            if (claims != null && claims.length > 0) {
                const clientNameSpace = claims.find(claim => claim.type == "df.ns")?.value;
                this.clientNameSpace = clientNameSpace ?? "UNKNOWN_NOTFOUND";
                this.setCulture(claims);
                this.loadMyAccess();
            }
        });
    }

    //Load a list of users
    private async getUsers(): Promise<UserListing> {
        const url = `${this.baseUrl}/api/${this.clientNameSpace}/users`;
        let listing: UserListing;
        await firstValueFrom(this.http.get<UserListing>(url))
            .then(l => { listing = l })
            .catch(e => {
                this.logRequestError('loadUsers', url, e);
            });
        return listing;
    }

    public async loadMyAccess(): Promise<void> {
        const url = `${this.baseUrl}/api/${this.clientNameSpace}/MyAccess`;
        try {
            const myAccess = await firstValueFrom(this.http.get<MyAccess>(url));
            this.myAccess = myAccess!;
            this.$myAccess.next(myAccess);
        } catch (e) {
            this.logRequestError('loadMyAccess', url, e);
            this.myAccess = null;
            this.$myAccess.next(null);
        }
    }

    public async getUser(id: string): Promise<UserV2> {
        const url = `${this.baseUrl}/api/${this.clientNameSpace}/users/${id}`;
        let user: UserV2;
        await firstValueFrom(this.http.get<UserV2>(url))
            .then(u => { user = u })
            .catch(e => {
                this.logRequestError('getUser', url, e);
            });
        return user;
    }

    private async getRoles(): Promise<RoleListing> {
        const url = `${this.baseUrl}/api/${this.clientNameSpace}/roles`;
        let listing: RoleListing;
        await firstValueFrom(this.http.get<RoleListing>(url))
            .then(l => { listing = l })
            .catch(e => {
                this.logRequestError('loadRoles', url, e);
                 this.$disableAdminButtons.next(true);
            });
        return listing;
    }

    public async getRole(id): Promise<Role> {
        const url = `${this.baseUrl}/api/${this.clientNameSpace}/roles/${id}`;
        let role: Role;
        await firstValueFrom(this.http.get<Role>(url))
            .then(r => { role = r; })
            .catch(e => {
                this.logRequestError('getRole', url, e);
            });
        return role;
    }

    public async getFileGroups(): Promise<FileGroupListing> {
        const url = `${this.baseUrl}/api/${this.clientNameSpace}/fileGroups`;
        let listing: FileGroupListing;
        const userPromise = firstValueFrom(this.http.get<FileGroupListing>(url))
            .then(l => { listing = l })
            .catch(e => {
                this.logRequestError('loadFileGroups', url, e);
            });
        await userPromise;
        return listing;
    }

    public async loadUsers() {
        this.users = (await this.getUsers()).users;
    }

    public async loadRoles() {
        this.roles = (await this.getRoles()).roles;
    }

    public async loadFileGroups() {
        this.fileGroups = (await this.getFileGroups()).fileGroups;
    }

    public async loadTableData() {
        this.tables = (await this.getAllTableDataSetsForPermissions());
    }

    public async getAllTableDataSetsForPermissions(): Promise<TableData[]> {
        var url = `${this.baseUrl}/api/${this.clientNameSpace}/permissions/table-data`;
        try {
            return await this.http.get<TableData[]>(url).toPromise();
        } catch (e) {
            this.logRequestError('loadTableData', url, e);
            return [];
        }
    }

    public async createNewRole(name) {
        const url = `${this.baseUrl}/api/${this.clientNameSpace}/roles`;
        let newRole: Role;
        const postBody: { TenantId: string, Id: string, Name: string, Permissions: Permissions } = {
            TenantId: this.tenantService.tenant.id, Name: name,
            Id: "",
            Permissions: new Permissions
        };
        postBody.Permissions.securityAdmin = false;
        postBody.Permissions.fileGroups = new FileGroupPermissionListing();
        postBody.Permissions.fileGroups.admin = false;
        postBody.Permissions.fileGroups.groups = [];
        postBody.Permissions.tables = new TablePermissionListing();
        postBody.Permissions.tables.admin = false;
        postBody.Permissions.tables.dataSets = [];
        postBody.Permissions.integrationPermissions = new IntergrationPermissionListing()
        postBody.Permissions.integrationPermissions.admin = false;
        postBody.Permissions.integrationPermissions.integrations = [];
        postBody.Permissions.projectGroups = new ProjectGroupPermissionListing()
        postBody.Permissions.projectGroups.admin = false;
        postBody.Permissions.projectGroups.group = [];
        postBody.Permissions.projectGroups.permission = "";
        await firstValueFrom(this.http.post<any>(url, postBody))
            .then(r => { newRole = r; })
            .catch(e => {
                this.logRequestError('createNewRole', url, e);
            });
        return newRole;
    }

    public async loadIntegrations() {
        this.integrations = this.tenantService.tenant.integrations;
    }

    public async saveUpdatedUsers(users: UserV2[]) {
        for (let updatedUser of users) {
            const url = `${this.baseUrl}/api/${this.clientNameSpace}/users/${updatedUser.id}`;
            const rolesPayload = Array.isArray(updatedUser.roles)
                ? updatedUser.roles
                : (({ enable, stateUpdated, ...rest }) => rest)(updatedUser.roles as any);
            const payload: UserV2 = {
                ...updatedUser,
                roles: rolesPayload
            };
            try {
                await firstValueFrom(this.http.put<UserV2>(url, payload).pipe(take(1)));
                
            } catch (error) {
                this.logRequestError('saveUpdatedUsers', url, error);
            }
            await this.loadMyAccess();
        }
    }

    public async saveUpdatedRoles() {
        for (let role of this.$updatedRoles.value) {
            const url = `${this.baseUrl}/api/${this.clientNameSpace}/roles/${role.id}`;
            try {
                role = await firstValueFrom(this.http.put<Role>(url, role).pipe(take(1)));
            } 
            catch (error) {
                this.logRequestError('saveUpdatedRoles', url, error);
            }
            this.updatedRoles = [];
            this.$updatedRoles.next([]);
        }
        await this.loadMyAccess();
    }

    public async syncUsers(): Promise<boolean> {
        let success = false;
        const url = `${this.baseUrl}/api/${this.clientNameSpace}/users/sync`;
        let subscription;

        try {
            subscription = this.loaderService.isLoading.subscribe(value => {
                // Stop interceptor from blocking the UI with the loader while we wait
                if (value) {
                    this.loaderService.isLoading.next(false);
                    subscription.unsubscribe();
                }
            })
            await firstValueFrom(this.http.post<any>(url, null))
                .then(async _ => {
                    await this.loadUsers();
                    success = true;
                })
                .catch(e => { this.logRequestError('syncUsers', url, e); });
        } finally {
            subscription?.unsubscribe();
        }
        return success;
    }

    public selectRole(role: Role) {
        this.selectedRole = role;
        this.$selectedRole.next(role);
    }
  
    public integrationAdminEnabled() {
        return this.selectedRole?.permissions?.integrationPermissions?.admin ?? false;
    }

    public addRoleToUpdatedCollection(role: Role) {
        if (!this.updatedRoles.some(r => r.id == role.id)) {
            this.updatedRoles.push(role);
            this.$updatedRoles.next(this.updatedRoles);
        }
    }

    private setCulture(claims: UserClaim[]) {
        const cultureObject = JSON.parse(claims.find(claim => claim.type == "df.culture").value);
        this.stringLocalizerService.setLanguage(cultureObject["Code"]);
    }

    getlocalHostAccess(): Observable<MyAccess | null> {
        return this.$myAccess.asObservable();
    }

    private sanitizeForLog(input: string): string {
        return input ? input.replace(/[\r\n]+/g, ' ') : '';
    }

    private logRequestError(operation: string, url: string, error: unknown): void {
        this.logger.error('AdminService', `${operation} failed`, error, {
            operation,
            endpointPath: this.sanitizeForLog(url)
        });
    }
}